import moment from 'moment'
import {
  __,
  always,
  allPass,
  anyPass,
  applySpec,
  complement,
  defaultTo,
  evolve,
  filter,
  find,
  flatten,
  flip,
  groupBy,
  identity,
  indexBy,
  ifElse,
  propSatisfies,
  map,
  mergeRight,
  mergeWithKey,
  omit,
  path,
  pick,
  pickAll,
  pipe,
  prop,
  propEq,
  reduce,
  values,
  when,
  without
} from 'ramda'
import { createSelector } from 'reselect'
import ENV_VARS from '@/config'
import {
  defaultIfEmpty,
  isFalsy,
  isNullOrEmpty,
  isTruthy,
  mapBy,
  mergeSpec,
  parseObjectData
} from '@/utils'
import formatDate from '@/utils/format-date'
import { formatPhoneObject } from '@/utils/format-phone'
import onlyIfDefined from '@/utils/only-if-defined'
import toArray from '@/utils/to-array'
import toBoolean from '@/utils/to-boolean'
import { flags } from '@/flags'

const showPad = flags.features.pad

/** HELPERS */
const formatSignature = raw => ({
  id: raw.id,
  userId: raw.user_id,
  userType: raw.user_type,
  contractId: raw.contract_id,
  createdAt: raw.created_at,
  url: `${ENV_VARS.BACKEND_SERVER}/contracts/\
${raw.contract_id}/signatures/${raw.id}/signature.png`
})

const formatFee = fee => ({
  id: fee.id,
  name: fee.name,
  price: fee.fee,
  frequency: fee.fee_frequency_txt_id
})

const formatAdditionalFees = pipe(
  filter(additionalFee => additionalFee.fee_txt_id === 'misc'),
  map(formatFee),
  toArray
)

const getFee = (id, data) => data?.find(item => item.fee_txt_id === id)

const getSelectedUsers = (landlords, users) =>
  pipe(values, map(prop('user_id')), map(prop(__, users)))(landlords)

const formatExtraUser = user => ({
  id: user.id,
  first_name: user.first_name,
  last_name: user.last_name,
  dob: user.dob,
  email: user.email_address,
  signatureRequired: isTruthy(user.signature_required),
  ...formatPhoneObject(user.phone_number, user.phone_country_code, user.phone_country_code_alpha)
})

const getLandlordId = landlords => onlyIfDefined(path([0], landlords), e => e.user_id)

const getLandlord = contract =>
  path(['users', getLandlordId(contract.contract_landlords)], contract)

const getCompany = contract => {
  const landlord = getLandlord(contract)
  if (!landlord) return null
  return path(['companies', landlord.company_id], contract)
}

const getSystemUsers = (usersList = [], users = {}) =>
  usersList.reduce(
    (accum, user) => {
      const key = hasSignatureRequired(user) ? 'signingTenants' : 'nonSigningTenants'
      return {
        ...accum,
        [key]: [...accum[key], users[user.user_id]]
      }
    },
    { signingTenants: [], nonSigningTenants: [] }
  )

const calculateStartDate = (startDate, listing) => {
  // if contract already has a start date return it
  if (startDate && moment(startDate).isValid()) return startDate

  // if listing availability date has not yet passed, use it
  if (moment(listing.availability_date).isAfter(moment())) return listing.availability_date

  // otherwise use the first day of current/next month
  const firstDayOfCurrentMonth = moment().startOf('month')
  const isFirstDayOfMonth = moment().date() === 1
  return isFirstDayOfMonth ? firstDayOfCurrentMonth : firstDayOfCurrentMonth.add(1, 'month')
}

const calculateEndDate = (endDate, listing) => {
  // if contract already has an end date return it
  if (endDate && moment(endDate).isValid()) return endDate

  // determine which date to use for the availability date
  let availabilityDate = ''
  if (moment(listing.availability_date).isAfter(moment())) {
    availabilityDate = listing.availability_date
  } else {
    const firstDayOfCurrentMonth = moment().startOf('month')
    const isFirstDayOfMonth = moment().date() === 1
    availabilityDate = isFirstDayOfMonth
      ? firstDayOfCurrentMonth
      : firstDayOfCurrentMonth.add(1, 'month')
  }

  // return calculated end date (availability date + minimum lease period - 1 day)
  if (listing.min_lease_period && listing.min_lease_period_type) {
    return moment(availabilityDate)
      .add(Number(listing.min_lease_period), listing.min_lease_period_type)
      .subtract(1, 'days')
  }

  // if no defined minimum lease period, return empty string
  return ''
}

/** FORMATTERS */
const formatContract = (raw = {}) => ({
  id: raw.id,
  state_machine: raw.state_machine,
  company: getCompany(raw),
  listing: raw.listings,
  unit: raw.units,
  building: raw.buildings,
  signatures: map(formatSignature, values(raw.contract_signatures)),
  listingId: raw.listing_id,
  tenancyStartDate: formatDate(raw.start_date),
  tenancyEndDate: formatDate(raw.end_date),
  effective_date: formatDate(raw.effective_date),
  lease_end_action: raw.lease_end_action,
  lease_type: raw.lease_type,
  listing_contract_act_id: raw.listing_contract_act_id,
  price: raw.price,
  custom_fields: raw.custom_fields,
  price_frequency: raw.price_frequency,
  price_date_description: raw.price_date_description,
  security_deposit: raw.security_deposit,
  security_deposit_due_date: formatDate(raw.security_deposit_due_date),
  pet_deposit: raw.pet_deposit,
  pet_deposit_due_date: formatDate(raw.pet_deposit_due_date),
  pet_policy: toBoolean(raw.pet_policy),
  allow_cats: raw.allow_cats,
  allow_dogs: raw.allow_dogs,
  utilities: raw.contract_utilities,
  customUtilities: raw.contract_custom_utilities,
  features: raw.contract_features,
  vehicles: raw.contract_vehicles,
  customFeatures: raw.contract_custom_features,
  additional_fees: formatAdditionalFees(toArray(raw.contract_fees)),
  parking_fee: onlyIfDefined(getFee('parking', raw.contract_fees), formatFee),
  storage_fee: onlyIfDefined(getFee('storage', raw.contract_fees), formatFee),
  custom_addendums: toArray(raw.contract_addendums),
  landlords: getSelectedUsers(raw.contract_landlords, raw.users),
  extra_users: map(formatExtraUser, toArray(raw.contract_extra_users)),
  strata_plan_number: raw.strata_plan_number,
  strata_lot_number: raw.strata_lot_number,
  is_complete: ['active', 'signed'].includes(raw.state_machine),
  start_date: raw.start_date,
  end_date: raw.end_date,
  contract_terms_accepted: raw.contract_terms_accepted,
  ...(showPad && {
    pad_requested: raw.pad_requested,
    pad_active: raw.pad_active,
    payment_method: raw.payment_method,
    payment_method_info: raw.payment_method_info,
    billing_user_id: raw.billing_user_id
  }),
  ...getSystemUsers(raw.contract_rentals, raw.users)
})

const formatPackageInfo = (raw = {}) => ({
  contract: formatContract(raw.contract),
  packageFiles: raw.package_files,
  signers: raw.signers,
  recipients: raw.recipients,
  applicants: raw.applicants
})

/** SELECTORS */
const formatList = ({ listings: listing, units: unit, buildings: building, users, ...raw }) => {
  const landlords = reduce(
    (acc, item) => {
      const user = users[item.user_id]
      if (!user) return acc
      return [...acc, `${user.first_name} ${user.last_name}`]
    },
    [],
    raw?.contract_landlords ?? []
  ).join('\n')
  return {
    address: building.full_street_name,
    availability: listing.availability_date,
    bedrooms: unit.count_bedrooms,
    building_name: building.name || building.street_name,
    city: building.city,
    end_date: raw.end_date,
    gr_max_price: unit.gr_max_price,
    gr_max_size: unit.gr_max_size,
    gr_min_price: unit.gr_min_price,
    gr_min_size: unit.gr_min_size,
    gr_unit: unit.gr_unit,
    is_signed: true,
    lease_type: raw.lease_type,
    listing_contract_id: raw.id,
    listing_id: raw.listing_id,
    price: raw.price,
    size: unit.size,
    start_date: raw.start_date,
    state: building.state,
    state_machine: raw.state_machine,
    unit_number: unit.unit_number,
    landlords: landlords,
    pad_active: raw.pad_active
  }
}

export const activeContractsSelector = createSelector(
  omit(['completedSteps', 'signature', 'terms', 'contract_initials', 'loadedAll']),
  pipe(values, filter(complement(isNullOrEmpty)), map(formatList))
)

export const termsSelector = createSelector(
  prop('terms'),
  flip(prop('listingId')),
  (terms, listingId) => terms[listingId]
)

export const isStepCompleteSelector = createSelector(
  prop('completedSteps'),
  flip(prop('step')),
  (completedSteps, step) => completedSteps.indexOf(step) !== -1
)

// Selectors above are from previous implmentation
export const getContractSelector = createSelector(
  flip(prop('contractId')),
  identity,
  pipe(prop, defaultTo({}))
)

const CONTRACT_PROPS = [
  'contract_addendums',
  'contract_custom_features',
  'contract_custom_utilities',
  'contract_extra_users',
  'contract_features',
  'contract_fees',
  'contract_landlords',
  'contract_non_signing_tenants',
  'contract_rentals',
  'contract_utilities',
  'custom_fields',
  'start_date',
  'end_date',
  'lease_end_action',
  'lease_type',
  'parking',
  'pet_policy',
  'pet_deposit',
  'pet_deposit_due_date',
  'allow_cats',
  'allow_dogs',
  'price',
  'price_frequency',
  'price_date_description',
  'security_deposit',
  'security_deposit_due_date',
  'start_date',
  'storage',
  'strata_lot_number',
  'strata_plan_number',
  ...(showPad
    ? ['pad_active', 'pad_requested', 'payment_method', 'payment_method_info', 'billing_user_id']
    : [])
]

const hasTenantsFromApplication = propSatisfies(
  complement(isNullOrEmpty),
  'contract_signing_tenants'
)

const hasSignatureRequired = propEq('signature_required', '1')

const mergeIfFalsy = data => mergeWithKey((k, l, r) => when(isFalsy, always(r))(l), __, data)

const isAdditionalFee = propEq('fee_txt_id', 'misc')

const feeNotAvailable = propEq('fee_frequency_txt_id', 'not_available')

const feeFree = propEq('fee_frequency_txt_id', 'free')

const getFeeData = type => {
  return pipe(
    prop('contract_fees'),
    defaultTo([]),
    find(propEq('fee_txt_id', type)),
    when(
      isTruthy,
      pipe(
        pick(['fee_frequency_txt_id', 'fee_txt_id', 'name', 'fee']),
        mergeSpec({
          enabled: complement(feeNotAvailable),
          hasFee: complement(anyPass([feeNotAvailable, feeFree]))
        }),
        parseObjectData
      )
    )
  )
}

const getPetPolicy = ({
  pet_policy,
  pet_deposit,
  pet_deposit_due_date,
  allow_cats,
  allow_dogs
}) => {
  const formattedValues = {
    is_allowed: toBoolean(pet_policy),
    pet_limitations: !isNullOrEmpty(allow_cats) || !isNullOrEmpty(allow_dogs),
    pet_deposit,
    pet_deposit_due_date,
    allow_cats: allow_cats || 0,
    allow_dogs: allow_dogs || 0
  }
  return { pet_policy: formattedValues }
}

const getCustomFields = customFields => {
  const allCustomFields = pipe(
    defaultTo([]),
    indexBy(prop('field_txt_id')),
    map(prop('field_value'))
  )(customFields)
  const customFieldTypes = pipe(
    defaultTo([]),
    indexBy(prop('field_txt_id')),
    map(prop('field_type'))
  )(customFields)
  return { ...allCustomFields, custom_field_types: customFieldTypes }
}

export const contractDataSelector = (contractData, listingData, applicationData = {}) => {
  return createSelector(pickAll(CONTRACT_PROPS), prop('users'), (contract, users) =>
    pipe(
      pickAll(
        without(
          [
            'custom_fields',
            'pet_policy',
            'pet_deposit',
            'pet_deposit_due_date',
            'allow_cats',
            'allow_dogs'
          ],
          CONTRACT_PROPS
        )
      ),
      mergeRight(getPetPolicy(contract)),
      mergeRight(getCustomFields(contract.custom_fields)),
      evolve({
        contract_landlords: mapBy('user_id', users)
      }),
      mergeSpec({
        storage: getFeeData('storage'),
        parking: getFeeData('parking'),
        contract_signing_tenants: data =>
          ifElse(hasTenantsFromApplication, prop('contract_signing_tenants'), () =>
            pipe(
              prop('contract_rentals'),
              defaultTo([]),
              filter(hasSignatureRequired),
              mapBy('user_id', users)
            )(data)
          )(applicationData),
        contract_non_signing_tenants: pipe(
          prop('contract_rentals'),
          defaultTo([]),
          filter(complement(hasSignatureRequired)),
          mapBy('user_id', users)
        ),
        additional_fees: pipe(prop('contract_fees'), defaultTo([]), filter(isAdditionalFee))
      }),
      mergeIfFalsy(listingData),
      mergeIfFalsy(applicationData),
      evolve({
        contract_signing_tenants: defaultIfEmpty(applicationData.contract_signing_tenants),
        start_date: pipe(() => calculateStartDate(contract.start_date, listingData), formatDate),
        end_date: pipe(() => calculateEndDate(contract.end_date, listingData), formatDate)
      })
    )(contract)
  )(contractData)
}

export const contractViewSelector = createSelector(getContractSelector, formatContract)

export const contractPackageSelector = createSelector(getContractSelector, formatPackageInfo)

export const listingContractsSelector = createSelector(
  omit(['completedSteps', 'signature', 'terms', 'loadedAll']),
  flip(prop('listingId')),
  (contracts, listingId) => pipe(values, filter(propEq('listing_id', listingId)))(contracts)
)

export const groupListingContractsSelector = createSelector(
  listingContractsSelector,
  groupBy(prop('state_machine'))
)

export const nextContractSelector = createSelector(
  listingContractsSelector,
  pipe(
    groupBy(prop('state_machine')),
    omit(['history', 'active']),
    values,
    flatten,
    find(propEq('contract_type', 'extension'))
  )
)

export const lastActiveContractSelector = createSelector(
  listingContractsSelector,
  find(propEq('state_machine', 'active'))
)

const formatPadContracts = ({ listings: listing, units: unit, buildings: building, ...raw }) => {
  return {
    listing_contract_id: raw.id,
    listing_id: raw.listing_id,
    price: raw.price,
    price_frequency: raw.price_frequency,
    price_date_description: raw.price_date_description,
    size: unit.size,
    start_date: raw.start_date,
    end_date: raw.end_date,
    full_street_name: building.full_street_name,
    zip: building.zip,
    unit_number: unit.unit_number,
    bedrooms: unit.count_bedrooms,
    count_all_bathrooms: unit.count_all_bathrooms,
    furnished: listing.furnished,
    building_name: building.name || building.street_name,
    city: building.city,
    gr_max_price: unit.gr_max_price,
    gr_max_size: unit.gr_max_size,
    gr_min_price: unit.gr_min_price,
    gr_min_size: unit.gr_min_size,
    gr_unit: unit.gr_unit,
    state: building.state,
    state_machine: raw.state_machine,
    pad_requested: raw.pad_requested,
    payment_method: raw.payment_method,
    payment_method_info: raw.payment_method_info,
    billing_user_id: raw.billing_user_id
  }
}

const userContractsSelector = createSelector(
  omit(['completedSteps', 'signature', 'terms', 'contract_initials', 'loadedAll']),
  flip(prop('userId')),
  (contracts, userId) => {
    return pipe(values, filter(propEq('billing_user_id', userId)))(contracts)
  }
)

export const userPadEnabledContractsSelector = createSelector(
  userContractsSelector,
  pipe(
    values,
    filter(propEq('pad_requested', true)),
    applySpec({
      contracts: pipe(
        filter(anyPass([propEq('state_machine', 'active'), propEq('state_machine', 'signed')])),
        map(formatPadContracts)
      ),
      // Want to get all contracts that tenant has signed with PAD, but not landlord.
      // Will need to display these when user updates PAD even if it is not final.
      sent: pipe(
        filter(allPass([propEq('state_machine', 'sent'), propEq('payment_method', 'pad')])),
        map(formatPadContracts)
      )
    })
  )
)
